import { strictify } from '~/utils/strictify'
import { z } from 'zod'
import {
  PhoneVerifyResendSuccessSchema,
  PhoneVerifyStatusSuccessSchema,
  PhoneVerifySuccessSchema,
  type PhoneVerifyResendSuccess,
  type PhoneVerifyStatusSuccess,
  type PhoneVerifySuccess
} from '~/services/api/generated'
import { PhoneVerify } from '~/services/api/contracts/requests/PhoneVerify'

/**
 * Narrower than the generated schemas (`result_code: ResultCodeSchema`, the
 * full closed enum, since the OpenAPI contract types every success envelope
 * against the shared `ResultCode` component). Re-validating with `z.enum`
 * here -- after the generated schema has already validated the wire shape --
 * is the boundary normalization step that gives callers the narrow
 * `ResultCode` literal a given controller action actually returns, mirroring
 * transport.ts's `ForgotPasswordResultCodeSchema`.
 */
const PhoneVerifyResendResultCodeSchema = z.enum([
  'PHONE_VERIFIED',
  'PHONE_VERIFICATION_SENT',
  'PHONE_VERIFICATION_NOT_REQUIRED'
])

const PhoneVerifyResultCodeSchema = z.enum(['PHONE_VERIFIED', 'PHONE_VERIFICATION_NOT_REQUIRED'])

type PhoneVerifyResult = Omit<PhoneVerifySuccess, 'result_code' | 'data'> & {
  result_code: z.infer<typeof PhoneVerifyResultCodeSchema>
  data: PhoneVerifySuccess['data']
}
type PhoneVerifyResendResult = Omit<PhoneVerifyResendSuccess, 'result_code'> & {
  result_code: z.infer<typeof PhoneVerifyResendResultCodeSchema>
}

/**
 * Phone verification service.
 *
 * Wraps the authenticated `/api/user/phone/*` endpoints used by the SMS
 * account flows (APP-352). These live under `/api/user`, not `/api/dashboard`.
 *
 * Usage:
 *   const { phoneVerification } = useApiClient()
 *   await phoneVerification().verify('123456')
 */
export function usePhoneVerificationService() {
  const baseURL = `${useRuntimeConfig().public.apiBaseUrl}/api/user/phone`
  const sanctumClient = useApiRequest()

  /**
   * POST /api/user/phone/verify
   * Submit the 6-digit code (concatenated into a single string).
   */
  async function verify(code: string): Promise<PhoneVerifyResult> {
    const response = strictify(PhoneVerifySuccessSchema).parse(
      await sanctumClient(`${baseURL}/verify`, {
        method: 'POST',
        body: PhoneVerify({}).parse({ code })
      })
    )
    return {
      ...response,
      result_code: PhoneVerifyResultCodeSchema.parse(response.result_code),
      data: response.data
    }
  }

  /**
   * POST /api/user/phone/verify/resend
   * Request a fresh verification code. Invalidates any prior code.
   */
  async function resend(): Promise<PhoneVerifyResendResult> {
    const response = strictify(PhoneVerifyResendSuccessSchema).parse(
      await sanctumClient(`${baseURL}/verify/resend`, {
        method: 'POST'
      })
    )
    return {
      ...response,
      result_code: PhoneVerifyResendResultCodeSchema.parse(response.result_code)
    }
  }

  /**
   * GET /api/user/phone/verify/status
   * Whether phone verification is currently required at all (mirrors
   * PhoneVerificationService::isEnabled() on the API). Used by the phone
   * verification gate and the profile-settings phone-change flow to decide
   * whether to show verification UI, rather than routing into it and letting
   * it no-op (APP-1401).
   */
  async function status(): Promise<PhoneVerifyStatusSuccess> {
    return strictify(PhoneVerifyStatusSuccessSchema).parse(
      await sanctumClient(`${baseURL}/verify/status`, {
        method: 'GET'
      })
    )
  }

  return {
    verify,
    resend,
    status
  }
}
